// ✅ ClientPaymentGatewaySetup.jsx
// ✅ Added EXTERNAL_REDIRECT support
// - For bank/BillDesk collect URL like: https://payments.billdesk.com/bdcollect/pay?p1=xxxx
// - No amount update / no webhook needed
// - Backend should store ExternalRedirectUrl and return it during payment creation

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
} from "@mui/material";
import { API_URL } from "./config";

export default function ClientPaymentGatewaySetup() {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.9 },
  });

  const token = localStorage.getItem("auth");

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [rows, setRows] = useState([]);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [subcampuses, setSubcampuses] = useState([]);

  const [form, setForm] = useState({
    SubCampusCode: "",
    Gateway: "RAZORPAY",
    Mode: "LIVE",
    IsActive: 1,

    RazorpayKeyId: "",
    RazorpayKeySecret: "",
    WebhookSecret: "",

    PaytmMid: "",
    PaytmMerchantKey: "",
    PaytmWebsiteName: "",
    PaytmClientId: "",

    BilldeskMerchantId: "",
    BilldeskSecurityId: "",
    BilldeskChecksumKey: "",
    BilldeskGatewayUrl: "",
    BilldeskReturnUrl: "",
    BilldeskChecksumAlg: "SHA256_HEX",

    // ✅ New simple redirect gateway field
    ExternalRedirectUrl: "",
  });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadSubcampuses = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/payments/admin/subcampuses`, { headers });
      setSubcampuses(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadConfigs = async () => {
    setError("");
    try {
      const params = { Mode: form.Mode };

      if (form.SubCampusCode !== "__ALL__") {
        params.SubCampusCode = form.SubCampusCode || "";
      }

      const { data } = await axios.get(`${API_URL}/api/payments/admin/gateway-configs`, {
        headers,
        params,
      });

      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError("Failed to load gateway configs");
    }
  };

  useEffect(() => {
    loadSubcampuses();
    loadConfigs();
    // eslint-disable-next-line
  }, []);

  const validateBeforeSave = () => {
    if (!form.Gateway) return "Gateway is required.";

    if (form.Gateway === "EXTERNAL_REDIRECT") {
      const url = String(form.ExternalRedirectUrl || "").trim();

      if (!url) return "Redirect URL is required.";

      if (!/^https:\/\/.+/i.test(url)) {
        return "Redirect URL must start with https://";
      }
    }

    return "";
  };

  const save = async () => {
    setError("");
    setInfo("");

    const validationError = validateBeforeSave();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/payments/admin/gateway-config`,
        {
          ...form,
          SubCampusCode:
            form.SubCampusCode && form.SubCampusCode !== "__ALL__" ? form.SubCampusCode : "",
          ExternalRedirectUrl: String(form.ExternalRedirectUrl || "").trim(),
        },
        { headers }
      );

      setInfo("Saved successfully.");
      await loadConfigs();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Save failed");
    }
  };

  const deleteConfig = async (gatewayAccountId) => {
    if (!gatewayAccountId) return;

    const ok = window.confirm("Delete this gateway config? This cannot be undone.");
    if (!ok) return;

    setError("");
    setInfo("");
    setDeleteBusyId(gatewayAccountId);

    try {
      await axios.delete(`${API_URL}/api/payments/admin/gateway-config/${gatewayAccountId}`, {
        headers,
      });

      setInfo("Deleted successfully.");
      await loadConfigs();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Delete failed");
    } finally {
      setDeleteBusyId(null);
    }
  };

  const showRazorpay = form.Gateway === "RAZORPAY";
  const showPaytm = form.Gateway === "PAYTM";
  const showBilldesk = form.Gateway === "BILLDESK";
  const showExternalRedirect = form.Gateway === "EXTERNAL_REDIRECT";

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Client Payment Gateway Setup
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Configure Razorpay / Paytm / BillDesk / External Redirect per campus & mode
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={loadConfigs}>
              Refresh
            </Button>
            <Button variant="contained" sx={primaryBtnSx} onClick={save}>
              Save
            </Button>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {!!info && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setInfo("")}>
              {info}
            </Alert>
          )}

          <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2, position: "relative", overflow: "hidden" }}>
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 6,
                background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
                opacity: 0.95,
              }}
            />

            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Add / Update Gateway
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>SubCampus</InputLabel>
                    <Select
                      label="SubCampus"
                      value={form.SubCampusCode || ""}
                      onChange={(e) => setForm((p) => ({ ...p, SubCampusCode: e.target.value }))}
                    >
                      <MenuItem value="">Campus Default</MenuItem>
                      <MenuItem value="__ALL__">All (for list)</MenuItem>

                      {subcampuses.map((sc) => (
                        <MenuItem key={sc.SubCampusCode} value={sc.SubCampusCode}>
                          {sc.SubCampusCode} — {sc.SubCampusName || ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Gateway</InputLabel>
                    <Select
                      label="Gateway"
                      value={form.Gateway}
                      onChange={(e) => setForm((p) => ({ ...p, Gateway: e.target.value }))}
                    >
                      <MenuItem value="RAZORPAY">Razorpay</MenuItem>
                      <MenuItem value="PAYTM">Paytm</MenuItem>
                      <MenuItem value="BILLDESK">BillDesk</MenuItem>
                      <MenuItem value="EXTERNAL_REDIRECT">External Redirect / Bank URL</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Mode</InputLabel>
                    <Select
                      label="Mode"
                      value={form.Mode}
                      onChange={(e) => setForm((p) => ({ ...p, Mode: e.target.value }))}
                    >
                      <MenuItem value="LIVE">LIVE</MenuItem>
                      <MenuItem value="TEST">TEST</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Is Active</InputLabel>
                    <Select
                      label="Is Active"
                      value={form.IsActive}
                      onChange={(e) => setForm((p) => ({ ...p, IsActive: Number(e.target.value) }))}
                    >
                      <MenuItem value={1}>Active</MenuItem>
                      <MenuItem value={0}>Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {showRazorpay && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="Razorpay Key ID"
                        fullWidth
                        sx={inputSx}
                        value={form.RazorpayKeyId}
                        onChange={(e) => setForm((p) => ({ ...p, RazorpayKeyId: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="Razorpay Key Secret"
                        fullWidth
                        sx={inputSx}
                        value={form.RazorpayKeySecret}
                        onChange={(e) => setForm((p) => ({ ...p, RazorpayKeySecret: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        label="Razorpay Webhook Secret (optional)"
                        fullWidth
                        sx={inputSx}
                        value={form.WebhookSecret}
                        onChange={(e) => setForm((p) => ({ ...p, WebhookSecret: e.target.value }))}
                      />
                    </Grid>
                  </>
                )}

                {showPaytm && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="Paytm MID"
                        fullWidth
                        sx={inputSx}
                        value={form.PaytmMid}
                        onChange={(e) => setForm((p) => ({ ...p, PaytmMid: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="Paytm Merchant Key"
                        fullWidth
                        sx={inputSx}
                        value={form.PaytmMerchantKey}
                        onChange={(e) => setForm((p) => ({ ...p, PaytmMerchantKey: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="Paytm Website Name"
                        fullWidth
                        sx={inputSx}
                        value={form.PaytmWebsiteName}
                        onChange={(e) => setForm((p) => ({ ...p, PaytmWebsiteName: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="Paytm ClientId (optional)"
                        fullWidth
                        sx={inputSx}
                        value={form.PaytmClientId}
                        onChange={(e) => setForm((p) => ({ ...p, PaytmClientId: e.target.value }))}
                      />
                    </Grid>
                  </>
                )}

                {showBilldesk && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="BillDesk Merchant ID"
                        fullWidth
                        sx={inputSx}
                        value={form.BilldeskMerchantId}
                        onChange={(e) => setForm((p) => ({ ...p, BilldeskMerchantId: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="BillDesk Security ID (optional)"
                        fullWidth
                        sx={inputSx}
                        value={form.BilldeskSecurityId}
                        onChange={(e) => setForm((p) => ({ ...p, BilldeskSecurityId: e.target.value }))}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="BillDesk Checksum Key"
                        fullWidth
                        sx={inputSx}
                        value={form.BilldeskChecksumKey}
                        onChange={(e) => setForm((p) => ({ ...p, BilldeskChecksumKey: e.target.value }))}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" sx={inputSx}>
                        <InputLabel>Checksum Alg</InputLabel>
                        <Select
                          label="Checksum Alg"
                          value={form.BilldeskChecksumAlg}
                          onChange={(e) => setForm((p) => ({ ...p, BilldeskChecksumAlg: e.target.value }))}
                        >
                          <MenuItem value="SHA256_HEX">SHA256_HEX</MenuItem>
                          <MenuItem value="SHA256_BASE64">SHA256_BASE64</MenuItem>
                          <MenuItem value="MD5_HEX">MD5_HEX</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="BillDesk Gateway URL"
                        fullWidth
                        sx={inputSx}
                        value={form.BilldeskGatewayUrl}
                        onChange={(e) => setForm((p) => ({ ...p, BilldeskGatewayUrl: e.target.value }))}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="BillDesk Return URL (backend endpoint)"
                        fullWidth
                        sx={inputSx}
                        value={form.BilldeskReturnUrl}
                        onChange={(e) => setForm((p) => ({ ...p, BilldeskReturnUrl: e.target.value }))}
                        helperText="Example: https://your-api-domain/api/payments/billdesk/return"
                      />
                    </Grid>
                  </>
                )}

                {showExternalRedirect && (
                  <>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        This option only redirects the student/admin to the configured bank payment URL.
                        It will not create a gateway order, verify payment, or automatically update fee paid amount.
                      </Alert>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        label="External Redirect URL"
                        placeholder="https://payments.billdesk.com/bdcollect/pay?p1=xxxxxxxx"
                        fullWidth
                        sx={inputSx}
                        value={form.ExternalRedirectUrl}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, ExternalRedirectUrl: e.target.value }))
                        }
                        helperText="Use this for bank-provided payment links or BillDesk collect links."
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1.2, borderColor: "rgba(25,118,210,0.12)" }} />
                  <Button variant="contained" onClick={save} sx={primaryBtnSx}>
                    Save Gateway Config
                  </Button>
                  <Button variant="outlined" onClick={loadConfigs} sx={{ ...outlinedBtnSx, ml: 1 }}>
                    Refresh List
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Existing Configs
            </Typography>
            <Divider sx={{ mb: 1.5, borderColor: "rgba(25,118,210,0.12)" }} />

            {rows.length === 0 ? (
              <Typography sx={{ opacity: 0.8 }}>No configs found.</Typography>
            ) : (
              <Paper elevation={0} sx={tableWrapSx}>
                <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                  <TableContainer component="div">
                    <Table size="small" sx={{ minWidth: 1000 }}>
                      <TableHead>
                        <TableRow sx={theadRowSx}>
                          <TableCell>#</TableCell>
                          <TableCell>SubCampus</TableCell>
                          <TableCell>Gateway</TableCell>
                          <TableCell>Mode</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Updated</TableCell>
                          <TableCell>Details</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {rows.map((r, idx) => {
                          const active = !!r.IsActive;

                          return (
                            <TableRow
                              key={r.GatewayAccountId || `${r.Gateway}_${r.Mode}_${idx}`}
                              sx={zebraRowSx(idx)}
                            >
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>
                                {r.SubCampusCode || "Campus Default"}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 900 }}>{r.Gateway}</TableCell>
                              <TableCell>{r.Mode}</TableCell>

                              <TableCell>
                                <Chip
                                  size="small"
                                  label={active ? "ACTIVE" : "INACTIVE"}
                                  sx={{
                                    fontWeight: 900,
                                    backgroundColor: active
                                      ? "rgba(46,125,50,0.12)"
                                      : "rgba(0,0,0,0.08)",
                                    border: active
                                      ? "1px solid rgba(46,125,50,0.25)"
                                      : "1px solid rgba(0,0,0,0.12)",
                                    color: active ? "rgba(46,125,50,0.95)" : "rgba(0,0,0,0.70)",
                                  }}
                                />
                              </TableCell>

                              <TableCell sx={{ whiteSpace: "nowrap" }}>{r.UpdatedAt || "-"}</TableCell>

                              <TableCell sx={{ maxWidth: 560 }}>
                                {r.Gateway === "EXTERNAL_REDIRECT" ? (
                                  <Typography sx={{ fontSize: "0.85rem", opacity: 0.85 }}>
                                    Redirect URL: {r.ExternalRedirectUrl || r.ExternalUrl || "-"}
                                  </Typography>
                                ) : r.Gateway === "BILLDESK" ? (
                                  <Typography sx={{ fontSize: "0.85rem", opacity: 0.85 }}>
                                    URL: {r.BilldeskGatewayUrl || "-"} | Return:{" "}
                                    {r.BilldeskReturnUrl || "-"} | Alg: {r.BilldeskChecksumAlg || "-"}
                                  </Typography>
                                ) : r.Gateway === "RAZORPAY" ? (
                                  <Typography sx={{ fontSize: "0.85rem", opacity: 0.85 }}>
                                    KeyId:{" "}
                                    {r.RazorpayKeyId
                                      ? `${String(r.RazorpayKeyId).slice(0, 8)}…`
                                      : "-"}{" "}
                                    | Webhook: {r.WebhookSecret ? "Yes" : "No"}
                                  </Typography>
                                ) : r.Gateway === "PAYTM" ? (
                                  <Typography sx={{ fontSize: "0.85rem", opacity: 0.85 }}>
                                    MID:{" "}
                                    {r.PaytmMid ? `${String(r.PaytmMid).slice(0, 8)}…` : "-"} |
                                    Website: {r.PaytmWebsiteName || "-"}
                                  </Typography>
                                ) : (
                                  <Typography sx={{ fontSize: "0.85rem", opacity: 0.85 }}>—</Typography>
                                )}
                              </TableCell>

                              <TableCell align="right">
                                <Button
                                  variant="outlined"
                                  color="error"
                                  onClick={() => deleteConfig(r.GatewayAccountId)}
                                  disabled={!r.GatewayAccountId || deleteBusyId === r.GatewayAccountId}
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 900,
                                    borderColor: "rgba(244,67,54,0.35)",
                                    color: "rgba(244,67,54,0.90)",
                                    "&:hover": {
                                      borderColor: "rgba(244,67,54,0.55)",
                                      backgroundColor: "rgba(244,67,54,0.04)",
                                    },
                                  }}
                                >
                                  {deleteBusyId === r.GatewayAccountId ? "Deleting..." : "Delete"}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            )}
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
}